import enclosure from '../shared/placeVisibility.js';

const idOf = place => place?.idEntity;
const omittedCardTypes = new Set(['road', 'river', 'passage', 'fortification', 'nation']);

// Follow exact authored containment links, including nested spaces. Labels and
// physical proximity never decide whether a card belongs beneath a settlement.
export const settlementSubPlaceIds = (settlementId, places = []) => {
  const children = new Map(), records = [...places], visited = new Set();
  const link = (parent, child) => {
    if (!parent || !child) return;
    if (!children.has(parent)) children.set(parent, new Set());
    children.get(parent).add(child);
  };
  for (let index = 0; index < records.length; index++) {
    const place = records[index];
    if (!place || visited.has(place)) continue;
    visited.add(place);
    for (const parent of enclosure.parentIds(place)) link(parent, idOf(place));
    for (const child of place.composedOfChildren || []) {
      link(idOf(place), typeof child === 'string' ? child : idOf(child));
      if (typeof child === 'object') records.push(child);
    }
    for (const child of place.childPlaceIds || []) link(idOf(place), child);
  }
  const settlementTypes = new Set(['settlement', 'town', 'city', 'village', 'hamlet']);
  const roots = new Set([settlementId, ...records.filter(place => settlementTypes.has(String(place?.placeType || '').trim().toLowerCase())).map(idOf)].filter(Boolean));
  const descendants = new Set(), pending = [...roots], seen = new Set(pending);
  for (let index = 0; index < pending.length; index++) for (const child of children.get(pending[index]) || []) {
    if (seen.has(child)) continue;
    seen.add(child); descendants.add(child); pending.push(child);
  }
  return descendants;
};

// Knowledge and route sightings are durable; card visibility is only valid at
// the position and clock for which the server produced it.
const withinTolerance = (left, right, tolerance) => left != null && right != null
  && Number.isFinite(Number(left)) && Number.isFinite(Number(right))
  && Math.abs(Number(left) - Number(right)) <= tolerance;

export const visiblePlaceIdsAt = (visibility, currentLocation, currentMinute) => {
  const point = currentLocation?.location || currentLocation;
  // Allow harmless serialization drift and small position updates, while
  // rejecting snapshots from another location or materially different time.
  if (!point || !visibility?.location || !withinTolerance(visibility.minute, currentMinute, 0.01)
    || ['x', 'z'].some(axis => !withinTolerance(point[axis], visibility.location[axis], 0.1))
    || (point.y != null && visibility.location.y != null && !withinTolerance(point.y, visibility.location.y, 0.1))
    || (visibility.currentPlaceId && currentLocation?.placeId && visibility.currentPlaceId !== currentLocation.placeId)) return [];
  return visibility.placeIds || [];
};

export const buildVisiblePlaceCards = ({ containingPlaces = [], places = [], visibility, currentLocation, currentMinute }) => {
  const byId = new Map(places.map(place => [idOf(place), place]));
  const cards = new Map(containingPlaces.filter(Boolean).map(place => [idOf(place), place]));
  for (const id of visiblePlaceIdsAt(visibility, currentLocation, currentMinute)) {
    if (!cards.has(id) && byId.has(id)) cards.set(id, byId.get(id));
  }
  return [...cards.values()].filter(place => !omittedCardTypes.has(String(place.placeType || '').trim().toLowerCase()));
};
